<template>
	<div>
		<hr />
		<!-- Case Filters -->
		<CaseFilters @filters-updated="applyFilters"></CaseFilters>

		<!-- Loading State -->
		<div v-if="loading" class="text-center my-3">
			<div class="d-flex flex-column align-items-center">
				<div class="spinner-border text-primary mb-3" role="status"></div>
				<span class="text-muted">{{ __('Fetching all archived cases...') }}</span>
			</div>
		</div>

		<!-- Empty State -->
		<div v-if="!loading && cases != null && cases.length === 0" class="text-center my-3">
			{{ __('No cases have been archived yet') }}
		</div>

		<!-- Case Cards -->
		<div v-if="cases != null && !loading && cases.length > 0" class="container">
			<div v-for="caseItem in cases" :key="caseItem.id">
				<CaseCard :caseItem="caseItem" :currentRole="currentRole" @delete-case="deleteCase"
					@navigate-tab="navigateToTab" @restore-case="restoreCase" :is-archived="true" />
			</div>

			<!-- Pagination -->
			<pagination :last_page="pagination.last_page" :current_page="pagination.current_page"
				:num_items="cases.length" :num_total="pagination.total"></pagination>
		</div>
	</div>
</template>

<script>
import CaseFilters from './CaseFilters.vue';
import CasesService from '@/services/cases-service';
import CaseCard from './CaseCard.vue';

export default {
	components: {
		CaseFilters,
		CaseCard,
	},

	data() {
		return {
			cases: null,
			loading: false,
			filters: {},
			pagination: {
				current_page: 1,
				last_page: 1,
				per_page: 10,
				total: 0,
			},
		};
	},

	mounted() {
		this.$eventHub.$on('activateTab', this.handleTabActivation);
		this.$on('change-page', (page) => {
			this.getCases(page);
		});
	},

	beforeDestroy() {
		this.$eventHub.$off('activateTab', this.handleTabActivation);
		this.$off('change-page');
	},

	computed: {
		currentRole() {
			return Spark.state.currentTeam?.users?.find((user) => user.id === Spark.state.user?.id)?.pivot?.role ?? 'member';
		},
	},

	methods: {
		async handleTabActivation(tabId, caseId) {
			if (tabId === 'archived-cases') {
				await this.getCases();

				const caseId = new URLSearchParams(window.location.search).get('caseId');
				if (caseId) {
					this.navigateToTab('case-details', caseId);
				}
			}
		},

		async applyFilters(updatedFilters) {
			this.filters = { ...updatedFilters };
			await this.getCases();
		},

		async getCases(page = 1) {
			this.loading = true;

			const params = {
				...this.filters,
				page,
				archived: true,
			};

			try {
				const response = await CasesService.getCases(Spark.state.currentTeam.id, params);
				this.cases = response.data;
				this.pagination.current_page = response.current_page;
				this.pagination.last_page = response.last_page;
				this.pagination.total = response.total;
			} catch (error) {
				console.error('Error fetching cases:', error.message);
			} finally {
				this.loading = false;
			}
		},

		async restoreCase(caseToRestore) {
			const payload = { archive: false };
			this.loading = true;

			try {
				await CasesService.updateCase(
					Spark.state.currentTeam.id,
					caseToRestore.id,
					payload,
				);
				await this.getCases();
			} catch (error) {
				console.error('Error restoring the case:', error.message);
			} finally {
				this.loading = false;
			}
		},

		async deleteCase(caseToDelete) {
			swal(
				{
					title: `Are you sure you want to delete this case?`,
					text: 'This action cannot be undone!',
					icon: 'danger',
					showCancelButton: true,
					confirmButtonColor: '#cd3537',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Remove',
				},
				async (result) => {
					this.loading = true;
					if (!result) {
						this.loading = false;
						return;
					}
					
					try {
						await CasesService.deleteCase(Spark.state.currentTeam.id, caseToDelete.id);
						await this.getCases();
						swal('Case deleted successfully.', '', 'success');
					} catch (error) {
						console.error('Error deleting the case:', error.message);
					} finally {
						this.loading = false;
					}
				},
			);
		},

		navigateToTab(tab, data) {
			this.$eventHub.$emit('activateTab', tab, data);
			$(this.$el).closest('div[role="tabcard"]').removeClass('active');
			$(`#${tab}`).addClass('active');
		},
	},
};
</script>
